import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogRef } from '@angular/cdk/dialog';
import { Shared } from '@shared/exports';
import { Alerts } from '@services/alerts/alerts';
import { AutoRechazo, MarcaRechazo, MotivoRechazo, MultiagendaService, RechazarProspectoResponse, TipoRechazo } from 'src/app/modules/multiagenda/multiagenda.service';
import type { SelectItem } from '@appTypes';
export type TipoReprogramacion = '0' | '1';
export type YaCompro = 'si' | 'no' | 'futuro';
type RadioOption = { key: string; value: string };
@Component({
  selector: 'app-home-rechazar-prospecto',
  standalone: true,
  imports: [FormsModule, Shared],
  templateUrl: './home-rechazar-prospecto.html',
  styleUrl: './home-rechazar-prospecto.scss',
})
export class HomeRechazarProspecto implements OnInit {
  private readonly dialogRef = inject(DialogRef);
  private readonly multiagendaService = inject(MultiagendaService);
  private readonly alerts = inject(Alerts);

  // Inputs
  $idDistribuidor = input.required<string>({ alias: 'idDistribuidor' });
  $idEjecutivo = input.required<string>({ alias: 'idEjecutivo' });
  $idProspecto = input.required<string>({ alias: 'idProspecto' });
  $idMarca = input.required<string>({ alias: 'idMarca' });
  $nombreCompleto = input.required<string>({ alias: 'nombreCompleto' });

  // Signals
  $motivosRechazo = signal<SelectItem[]>([]);
  $motivoSeleccionado = signal<string>('');
  $observaciones = signal<string>('');
  $loading = signal<boolean>(false);
  $loadingCatalogos = signal<boolean>(false);
  $mostrarRechazoAFuturo = signal<boolean>(true);
  $yaCompro = signal<YaCompro | ''>('');
  $motivosDondeCompro = signal<RadioOption[]>([]);
  $motivosPorqueNoCompra = signal<RadioOption[]>([]);
  $dondeCompro = signal<string>('');
  $porqueNoCompra = signal<string>('');
  $marcas = signal<SelectItem[]>([]);
  $autos = signal<SelectItem[]>([]);
  $marcaSeleccionada = signal<string>('');
  $autoSeleccionado = signal<string>('');
  $loadingMarcas = signal<boolean>(false);
  $loadingAutos = signal<boolean>(false);
  $tiposNoPudeContactar = signal<SelectItem[]>([]);

  $dondeComproMismaMarca = computed(() => {
    const id = this.$dondeCompro();
    if (!id) return false;
    const opcion = this.$motivosDondeCompro().find(m => m.key === id);
    return (opcion?.value || '').toLowerCase().includes('misma marca');
  });

  readonly tiposOptions: { value: TipoReprogramacion; label: string }[] = [
    { value: '0', label: 'No Pude Contactar' },
    { value: '1', label: 'Si Pude Contactar' },
  ];
  $tipoSeleccionado = signal<TipoReprogramacion>('0');

  readonly yaCompoOptionsBase: { value: YaCompro; label: string }[] = [
    { value: 'si', label: 'Si' },
    { value: 'no', label: 'No' },
    { value: 'futuro', label: 'Comprará a futuro' },
  ];

  yaCompoOptions = computed(() =>
    this.yaCompoOptionsBase.filter(o => o.value !== 'futuro' || this.$mostrarRechazoAFuturo())
  );

  onTipoChange(value: string): void {
    this.$tipoSeleccionado.set(value as TipoReprogramacion);
    this.$yaCompro.set('');
    this.$dondeCompro.set('');
    this.$porqueNoCompra.set('');
    this.$marcaSeleccionada.set('');
    this.$autoSeleccionado.set('');
    this.$autos.set([]);
    this.$motivoSeleccionado.set('');
    this.$observaciones.set('');
    if (value === '0') {
      this.$motivosRechazo.set(this.$tiposNoPudeContactar());
    } else {
      this.$motivosRechazo.set([]);
    }
  }

  onYaCompoChange(value: string): void {
    this.$yaCompro.set(value as YaCompro);
    this.$dondeCompro.set('');
    this.$porqueNoCompra.set('');
    this.$marcaSeleccionada.set('');
    this.$autoSeleccionado.set('');
    this.$autos.set([]);
    this.$motivoSeleccionado.set('');
    this.$observaciones.set('');
    this.$motivosRechazo.set([]);
  }

  onDondeCompoChange(value: string): void {
    this.$dondeCompro.set(value);
    this.$marcaSeleccionada.set('');
    this.$autoSeleccionado.set('');
    this.$autos.set([]);
    this.$motivoSeleccionado.set('');
    this.cargarTiposPorMotivo(value);
    if (!this.$dondeComproMismaMarca() && this.$marcas().length === 0 && !this.$loadingMarcas()) {
      this.cargarMarcas();
    }
  }

  onPorqueNoCompraChange(value: string): void {
    this.$porqueNoCompra.set(value);
    this.$motivoSeleccionado.set('');
    this.cargarTiposPorMotivo(value);
  }

  onMarcaChange(value: string): void {
    this.$marcaSeleccionada.set(value);
    this.$autoSeleccionado.set('');
    this.$autos.set([]);
    if (value) {
      this.cargarAutos(value);
    }
  }

  onAutoChange(value: string): void {
    this.$autoSeleccionado.set(value);
  }

  private cargarTiposPorMotivo(idMotivoDeRechazo: string): void {
    if (!idMotivoDeRechazo) {
      this.$motivosRechazo.set([]);
      return;
    }
    this.$loadingCatalogos.set(true);
    this.multiagendaService.getTiposRechazo(this.$idDistribuidor(), idMotivoDeRechazo).subscribe({
      next: (response) => {
        if (response.success && response.tipos) {
          const items: SelectItem[] = response.tipos.map((t: TipoRechazo) => ({
            key: t.IdTipoDeRechazo,
            value: t.Nombre,
          }));
          this.$motivosRechazo.set(items);
        } else {
          this.$motivosRechazo.set([]);
        }
        this.$loadingCatalogos.set(false);
      },
      error: () => {
        this.$loadingCatalogos.set(false);
        this.alerts.show('error', { title: 'Error', msg: 'Error al cargar motivos de rechazo' });
      },
    });
  }

  private cargarMarcas(): void {
    this.$loadingMarcas.set(true);
    this.multiagendaService.getMarcasRechazo(this.$idDistribuidor()).subscribe({
      next: (response) => {
        if (response.success && response.marcas) {
          this.$marcas.set(response.marcas.map((m: MarcaRechazo) => ({
            key: m.IdMarca,
            value: m.Nombre,
          })));
        }
        this.$loadingMarcas.set(false);
      },
      error: () => {
        this.$loadingMarcas.set(false);
        this.alerts.show('error', { title: 'Error', msg: 'Error al cargar marcas' });
      },
    });
  }

  private cargarAutos(idMarca: string): void {
    this.$loadingAutos.set(true);
    this.multiagendaService.getAutosRechazo(this.$idDistribuidor(), idMarca).subscribe({
      next: (response) => {
        if (response.success && response.autos) {
          this.$autos.set(response.autos.map((a: AutoRechazo) => ({
            key: a.IdAuto,
            value: a.Nombre,
          })));
        }
        this.$loadingAutos.set(false);
      },
      error: () => {
        this.$loadingAutos.set(false);
        this.alerts.show('error', { title: 'Error', msg: 'Error al cargar autos' });
      },
    });
  }

  // Computed
  $puedeRechazar = computed(() => {
    if (this.$loading()) return false;

    const motivo = this.$motivoSeleccionado().trim();
    const obs = this.$observaciones().trim();

    if (this.$tipoSeleccionado() === '0') {
      return motivo.length > 0 && obs.length > 0;
    }

    const ya = this.$yaCompro();
    if (ya === '') return false;
    if (ya === 'futuro') return true;

    if (ya === 'si') {
      if (this.$dondeCompro() === '') return false;
      if (this.$dondeComproMismaMarca()) {
        return motivo.length > 0 && obs.length > 0;
      }
      return motivo.length > 0
        && this.$marcaSeleccionada() !== ''
        && this.$autoSeleccionado() !== ''
        && obs.length > 0;
    }

    if (ya === 'no') {
      if (this.$porqueNoCompra() === '') return false;
      return motivo.length > 0 && obs.length > 0;
    }

    return false;
  });

  ngOnInit(): void {
    this.cargarMotivosRechazo();
  }

  private cargarMotivosRechazo(): void {
    this.$loadingCatalogos.set(true);

    this.multiagendaService.getMotivosRechazo(
      this.$idDistribuidor(),
      this.$idEjecutivo()
    ).subscribe({
      next: (response) => {
        if (response.success && response.tipos) {
          const items: SelectItem[] = response.tipos.map((t: TipoRechazo) => ({
            key: t.IdTipoDeRechazo,
            value: t.Nombre,
          }));
          this.$motivosRechazo.set(items);
          this.$tiposNoPudeContactar.set(items);
        }
        if (Array.isArray(response.motivosDondeCompro)) {
          this.$motivosDondeCompro.set(response.motivosDondeCompro.map((m: MotivoRechazo) => ({
            key: m.IdMotivoDeRechazo,
            value: m.Nombre,
          })));
        }
        if (Array.isArray(response.motivosPorqueNoCompra)) {
          this.$motivosPorqueNoCompra.set(response.motivosPorqueNoCompra.map((m: MotivoRechazo) => ({
            key: m.IdMotivoDeRechazo,
            value: m.Nombre,
          })));
        }
        if (typeof response.mostrarRechazoAFuturo === 'boolean') {
          this.$mostrarRechazoAFuturo.set(response.mostrarRechazoAFuturo);
        }
        this.$loadingCatalogos.set(false);
      },
      error: () => {
        this.$loadingCatalogos.set(false);
        this.alerts.show('error', { title: 'Error', msg: 'Error al cargar motivos de rechazo' });
      },
    });
  }

  onMotivoChange(value: string): void {
    this.$motivoSeleccionado.set(value);
  }

  onObservacionesChange(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.$observaciones.set(target.value);
  }

  onRechazar(): void {
    if (!this.$puedeRechazar()) return;

    this.$loading.set(true);

    this.multiagendaService.rechazarProspecto({
      idDistribuidor: this.$idDistribuidor(),
      idProspecto: this.$idProspecto(),
      idMotivoRechazo: this.$motivoSeleccionado(),
      observacion: this.$observaciones(),
      idUsuario: this.$idEjecutivo(),
    }).subscribe({
      next: (response: RechazarProspectoResponse) => {
        this.$loading.set(false);
        if (response.success) {
          this.alerts.show('success', { title: 'Rechazado Correctamente', msg: response.message || 'El prospecto ha sido rechazado' });
          this.dialogRef.close(true);
        } else {
          this.alerts.show('error', { title: 'Error', msg: response.message || 'Error al rechazar el prospecto' });
        }
      },
      error: () => {
        this.$loading.set(false);
        this.alerts.show('error', { title: 'Error', msg: 'Error al rechazar el prospecto' });
      },
    });
  }
}
